import React from 'react';
import { styled, Grid } from '@mui/material';
import { CardWrapper } from 'src/pages/Welcome/style';
import { useGetCreditsOverviewQuery, useGetCreditsReportAsCostQuery } from 'src/redux/services/credits';
import { DateRange, shortTextFormat, toISOStringCET } from 'src/utils/date';
import CostUsageTable, { CostAndUsageSkeleton } from 'src/pages/Welcome/components/ConsumptionGraphs/CostUsageTable';
import FailureScreen from 'src/frontend-common/components/FailureScreen';
import ChartWrapper from 'src/frontend-common/components/Charts/Wrapper';
import { currencyFixedDigitsFormat, dotsFormat } from 'src/utils/number';
import useGetCurrency from 'src/frontend-common/hooks/useGetCurrency';

const GraphGrid = styled(Grid)(({ theme }) => ({
  minHeight: 400,
  '&>div': {
    '&>div': {
      padding: theme.spacing(3),
    },
    '&>span': {
      marginTop: theme.spacing(22),
    },
  },
}));

const renameScoreToOnboarding = (data: {
  [date: string]: any;
}): {
  [date: string]: any;
} => {
  const newData: { [date: string]: any } = {};

  for (const date in data) {
    if (data.hasOwnProperty(date)) {
      const obj = { ...data[date] };
      obj.onboarding = obj.score;
      delete obj.score;
      newData[date] = obj;
    }
  }

  return newData;
};
interface Props {
  dateRange: DateRange;
  setSumOfCost: React.Dispatch<React.SetStateAction<number | null>>;
}
const ConsumptionGraphs: React.FC<Props> = ({ dateRange, setSumOfCost }) => {
  const filters = {
    start_date: toISOStringCET(dateRange.startDate),
    end_date: toISOStringCET(dateRange.endDate),
  };

  const { data: creditsOverviewOld, isLoading: isCreditsOverviewLoading, isError: isCreditsOverviewError } = useGetCreditsOverviewQuery({ ...filters });

  const creditsOverview = creditsOverviewOld ? renameScoreToOnboarding(creditsOverviewOld) : creditsOverviewOld;

  const {
    data: creditsReportAsCostOld,
    isLoading: isCreditsReportAsCostLoading,
    isError: isCreditsReportAsCostError,
  } = useGetCreditsReportAsCostQuery({ ...filters });

  const creditsReportAsCost = creditsReportAsCostOld ? renameScoreToOnboarding(creditsReportAsCostOld) : creditsReportAsCostOld;
  const dateFormatter = (s: string): string => {
    const date = new Date(s);
    return shortTextFormat(date).toString();
  };

  const allValuesAreZero = (data: Record<string, Record<string, number>>): boolean => {
    return Object.values(data).every((entry) => Object.values(entry).every((value) => value === 0));
  };

  return (
    <CardWrapper container spacing={2}>
      <GraphGrid item xs={12} md={6} xl={4}>
        {(isCreditsOverviewLoading || isCreditsReportAsCostLoading) && <CostAndUsageSkeleton />}
        {(isCreditsOverviewError || isCreditsReportAsCostError) && <FailureScreen>An error occurred while fetching usage or cost data.</FailureScreen>}
        {creditsOverview && creditsReportAsCost && <CostUsageTable usage={creditsOverview} cost={creditsReportAsCost} setSumOfCost={setSumOfCost} />}
      </GraphGrid>
      <GraphGrid item xs={12} md={6} xl={8}>
        <ChartWrapper
          isError={isCreditsOverviewError || isCreditsReportAsCostError}
          isLoading={isCreditsOverviewLoading || isCreditsReportAsCostLoading}
          components={[
            {
              chart: {
                data: creditsOverview
                  ? Object.entries(creditsOverview).map(([key, value]) => ({
                      xAxisLabel: dateFormatter(key),
                      values: value,
                    }))
                  : [],
                type: 'barChart',
                stackedBars: true,
                cartesianGrid: { horizontalLines: true, verticalLines: false },
                tooltipValueFormatter: (n) => dotsFormat(n),
                tooltipFooterValueFormatter: (n) => dotsFormat(n),
                height: 658,
                tooltipFooter: 'max',
              },
              isEmpty: (creditsOverview && allValuesAreZero(creditsOverview)) || (creditsOverview && Object.entries(creditsOverview).length === 0),
              tabLabel: 'Lookups',
              title: 'Daily lookups',
            },
            {
              chart: {
                data: creditsReportAsCost
                  ? Object.entries(creditsReportAsCost).map(([key, value]) => ({
                      xAxisLabel: dateFormatter(key),
                      values: value,
                    }))
                  : [],
                type: 'barChart',
                stackedBars: true,
                cartesianGrid: { horizontalLines: true, verticalLines: false },
                tooltipValueFormatter: (n) =>
                  currencyFixedDigitsFormat(n, 2, {
                    value: useGetCurrency(),
                    currencyPosition: 'left',
                  }),
                tooltipFooterValueFormatter: (n) =>
                  currencyFixedDigitsFormat(n, 2, {
                    value: useGetCurrency(),
                    currencyPosition: 'left',
                  }),
                height: 600,
                tooltipFooter: 'sum',
              },
              isEmpty:
                (creditsReportAsCost && allValuesAreZero(creditsReportAsCost)) || (creditsReportAsCost && Object.entries(creditsReportAsCost).length === 0),
              tabLabel: 'Cost',
              title: 'Daily lookups',
            },
          ]}
        />
      </GraphGrid>
    </CardWrapper>
  );
};

export default ConsumptionGraphs;
